/**
 * THE LIVE RESEARCH STRIP'S READ — the one call the Main Chat makes about a
 * window, and the vocabulary it renders.
 *
 * THE SERVER IS THE CLOCK AND THE RECORD. `serverNow` travels with every
 * answer so the countdown is measured against the deployment's instant rather
 * than this device's, and a refresh cannot reset or extend a deadline. The
 * phase comes from rows the research engine wrote while it worked — never from
 * a timer here. Where no phase row exists, this module says `WAITING`, which
 * is the truthful generic state.
 *
 * THE POLL IS BOUNDED. Ten seconds while a window is open (the driver's own
 * segment cadence is sixty, so this cannot miss a phase for long), sixty
 * seconds otherwise, and nothing at all when the tab is hidden. A poll is one
 * owner-gated read; no model call and no provider request is behind it.
 */

import { readGateway } from './gateway';

export const RESEARCH_POLL_ACTIVE_MS = 10_000;
export const RESEARCH_POLL_IDLE_MS = 60_000;
/** A terminal window is offered as a completion notice for this long. */
export const RESEARCH_NOTICE_WINDOW_MS = 12 * 60 * 60 * 1_000;
/** How long a phase may be old and still animate. The driver's floor is sixty
 *  seconds; this is the workspace's own margin, stated rather than implied. */
export const RESEARCH_PHASE_LIVE_MS = 2 * 60 * 1_000;

export type ResearchPhase =
	| 'WAITING'
	| 'SELECTING'
	| 'PLANNING'
	| 'SEARCHING'
	| 'READING'
	| 'ANALYZING'
	| 'RECORDING'
	| 'FINISHING'
	| 'ENDED'
	// THE PHASES THE RECORD ACTUALLY WRITES BEYOND THE CORE SET. They were
	// omitted from the client type while `run-delegation.ts` and the authoring
	// lane wrote them, so the panel rendered raw uppercase tags. A closed type
	// that does not match what the server writes is a lie the compiler tells.
	| 'PLANNING_WORKERS'
	| 'DELEGATING'
	| 'RETRIEVING'
	| 'WORKER'
	| 'RECONCILING'
	| 'WORKERS_DECLINED'
	| 'AUTHORING'
	| 'SELECTION_FAILED'
	| 'RECOVERY';

export interface ResearchActivityEntry {
	phase: ResearchPhase;
	detail: string;
	at: string;
}

/** ONE SPECIALIST WORKER, AS THE RECORD SAYS. `state` is the record's own
 *  vocabulary; the strip renders it, never a percentage. */
export interface DelegationWorkerActivity {
	role: string;
	index: number;
	state:
		| 'ACTIVE'
		| 'COMPLETED'
		| 'INCOMPLETE'
		| 'FAILED'
		| 'REFUSED'
		| 'NOT_STARTED'
		| 'CANCELLED'
		| 'UNKNOWN';
	detail: string;
	at: string | null;
}

/**
 * WHAT THE SPECIALIST RECORDS SAY ABOUT ONE WINDOW.
 *
 * `observed: false` means no delegation decision or event exists for THIS run,
 * not that delegation is unavailable. `line` is computed by the deployment from
 * those records; the client never invents activity text.
 */
export interface DelegationActivity {
	wired: boolean;
	observed: boolean;
	phase:
		| 'PLANNING'
		| 'DISPATCHING'
		| 'WORKING'
		| 'RECONCILING'
		| 'COMPLETED'
		| 'CANCELLED'
		| 'FAILED'
		| null;
	line: string | null;
	workers: DelegationWorkerActivity[];
	activeCount: number;
	completedCount: number;
	reportCount: number;
	failedCount: number;
	notStartedCount: number;
	cancelledCount: number;
	plannedCount: number;
	runs: Array<{
		delegationId: string;
		state: string;
		roles: string[];
		startedAt: string;
		endedAt: string | null;
		stoppedReason: string | null;
	}>;
}

export interface ResearchRunStatus {
	runId: string;
	state: 'RUNNING' | 'COMPLETED' | 'STOPPED' | 'FAILED';
	deadlineAt: string;
	startedAt: string;
	endedAt: string | null;
	endReason: string | null;
	terminalReason: string | null;
	cancelRequested: boolean;
	segmentsRun: number;
	drySegments: number;
	lastSegmentAt: string | null;
	/** The latest recorded phase, or `WAITING` when none was written. */
	phase: ResearchPhase;
	detail: string;
	phaseAt: string | null;
	/** TRUE only when the phase is real, recent, and the window still allows
	 *  work. The strip's ring reads this, never the state name alone. */
	moving: boolean;
	activity: ResearchActivityEntry[];
	/** Absent on a deployment whose status route predates the specialist
	 *  summary; absent is read as "no records to show", never as "none ran". */
	delegation?: DelegationActivity;
}

export interface ResearchStatusPayload {
	wired: boolean;
	serverNow: string;
	active: ResearchRunStatus | null;
	latest: ResearchRunStatus | null;
}

export async function readResearchStatus(): Promise<
	{ ok: true; data: ResearchStatusPayload } | { ok: false; error: import('./gateway').GatewayError }
> {
	return await readGateway<ResearchStatusPayload>('/api/scout/gateway/research/status', {
		timeoutMs: 15_000,
	});
}

/** The instant the deployment reported, as epoch milliseconds. Falls back to
 *  this device's clock only when the server's value cannot be read — and the
 *  fallback is the conservative one for a countdown (it can shorten the
 *  visible time, never extend it). */
export function serverInstant(payload: ResearchStatusPayload): number {
	const parsed = Date.parse(payload.serverNow);
	return Number.isFinite(parsed) ? parsed : Date.now();
}

/**
 * The countdown, measured against the instant the deployment reported.
 *
 * `receivedAtMs` is when THIS browser received the payload. The offset between
 * the two clocks is taken once, here, and applied to the local tick — so the
 * countdown does not depend on the device agreeing with the deployment, and a
 * refresh reads the same deadline rather than resetting one.
 */
export function remainingMs(
	run: ResearchRunStatus,
	payload: ResearchStatusPayload,
	nowMs: number,
	receivedAtMs: number,
): number {
	const deadline = Date.parse(run.deadlineAt);
	if (!Number.isFinite(deadline)) return 0;
	const offset = serverInstant(payload) - receivedAtMs;
	return Math.max(0, deadline - (nowMs + offset));
}

export function formatRemaining(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1_000));
	const hours = Math.floor(total / 3_600);
	const minutes = Math.floor((total % 3_600) / 60);
	const seconds = total % 60;
	if (hours > 0) {
		return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	}
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** WHAT THE STRIP SAYS, DERIVED FROM THE RECORD AND NOT FROM A TIMER. */
export type ResearchStripReading =
	| {
			kind: 'live';
			label: 'Researching';
			detail: string;
			moving: boolean;
	  }
	| {
			kind: 'cancelling';
			label: 'Stopping';
			detail: string;
			moving: false;
	  }
	| {
			kind: 'ended';
			label: string;
			detail: string;
			moving: false;
			/** The run ended in a way the completion notice must not call a
			 *  success. */
			failed: boolean;
	  }
	| {
			kind: 'unobserved';
			label: 'Window ended';
			detail: string;
			moving: false;
	  };

export function readingOf(run: ResearchRunStatus): ResearchStripReading {
	if (run.state === 'RUNNING') {
		if (run.cancelRequested) {
			return {
				kind: 'cancelling',
				label: 'Stopping',
				detail: 'Your stop is recorded; the window ends at its next step and starts no further work.',
				moving: false,
			};
		}
		const deadline = Date.parse(run.deadlineAt);
		if (Number.isFinite(deadline) && deadline <= Date.now() && run.moving === false) {
			// THE CLOCK RAN OUT AND THE RECORD HAS NOT CLOSED YET. It is not
			// complete (a completion is a write), and it is not working either.
			return {
				kind: 'unobserved',
				label: 'Window ended',
				detail: 'The authorized time has run out; the record closes on the next tick.',
				moving: false,
			};
		}
		return {
			kind: 'live',
			label: 'Researching',
			detail: run.detail,
			moving: run.moving,
		};
	}
	if (run.state === 'STOPPED') {
		return {
			kind: 'ended',
			label: 'Research stopped',
			detail: 'You stopped this window. The record is in Research.',
			moving: false,
			failed: false,
		};
	}
	if (run.state === 'FAILED') {
		return {
			kind: 'ended',
			label: 'Research failed',
			detail: 'No segment could start. The record says why.',
			moving: false,
			failed: true,
		};
	}
	return {
		kind: 'ended',
		label: 'Research finished',
		detail: 'The authorized window ended. Results are in Research and Discoveries.',
		moving: false,
		failed: false,
	};
}

/** The server's clock at THIS instant, using the same offset the countdown
 *  uses. The one-line activity text's "is Scout's own step still recent?"
 *  question is answered against the deployment, not the device. */
export function serverNowAt(
	payload: ResearchStatusPayload,
	receivedAtMs: number,
	nowMs: number,
): number {
	return serverInstant(payload) + (nowMs - receivedAtMs);
}

const DELEGATION_PHASES = new Set([
	'PLANNING_WORKERS',
	'DELEGATING',
	'RETRIEVING',
	'WORKER',
	'RECONCILING',
	'WORKERS_DECLINED',
]);

/** Scout's own most recent step, excluding delegation machinery. Used by the
 *  expanded panel to show the parent and the agents in one place. */
export function scoutOwnActivity(
	run: ResearchRunStatus,
): ResearchActivityEntry | null {
	const entry = [...run.activity]
		.reverse()
		.find(
			(candidate) =>
				!DELEGATION_PHASES.has(candidate.phase) &&
				candidate.phase !== 'WAITING' &&
				candidate.phase !== 'ENDED',
		);
	return entry ?? null;
}

/** THE ONE LINE'S ACTIVITY HALF, composed from records only.
 *
 *  A delegation line replaces Scout's own detail when it is the only activity.
 *  When Scout's own last step is ALSO recent, the two are shown together —
 *  "Reading sources · 2 agents active" — because the records support both.
 *  When Scout's last step is old, it is NOT shown: an earlier reading that has
 *  already finished must not be painted as concurrent work.
 */
export function stripActivityText(run: ResearchRunStatus, serverNowMs: number): string {
	const line = run.delegation?.line ?? null;
	if (line === null) return run.detail;
	const own = scoutOwnActivity(run);
	if (own !== null) {
		const at = Date.parse(own.at);
		const age = serverNowMs - at;
		if (Number.isFinite(age) && age >= -5_000 && age <= RESEARCH_PHASE_LIVE_MS) {
			return `${own.detail.replace(/[.…]+$/, '')} · ${line}`;
		}
	}
	return line;
}

/** The links a completion notice offers, as real paths the shell resolves. */
export const RESEARCH_RESULT_LINKS = [
	{ label: 'Research', href: '/research' },
	{ label: 'Discoveries', href: '/discoveries/evidence' },
] as const;
